import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { alpha, Box, Button, IconButton, Typography } from '@mui/material';
import { Close, Timeline } from '@mui/icons-material';
import { getLiveRace } from '../services/api';

const LIVE_RED = '#F9696D';
/** "1" once the race-day tip is dismissed; the live banner stores the race it was dismissed for. */
const TIP_KEY = 'ltb.slipLiveTip.dismissed';
const LIVE_KEY = 'ltb.slipLiveNow.dismissed';

const read = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode / blocked storage: dismissed for this visit only
  }
};

/** The Slip Builder's banner under its header: "follow this slip live on race day" (dismissed for good), or "it's live
 * now" while this track's race is on (dismissed for that race). Reads the same one-off live check as the Simulator's
 * strip (no extra polling). */
export const SlipLiveNote: React.FC<{ site: string; onOpen: () => void }> = ({ site, onOpen }) => {
  const live = useQuery({
    queryKey: ['liveRaceStrip'],
    queryFn: getLiveRace,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const race = live.data;
  const liveHere = !!race?.live && !race.finished && race.site === site;
  const raceKey = String(race?.raceId ?? race?.site ?? '');
  const [tipDismissed, setTipDismissed] = useState(() => read(TIP_KEY) === '1');
  const [liveDismissed, setLiveDismissed] = useState(() => read(LIVE_KEY));

  // Wait for the live check, so the tip never shows and then turns into the live banner.
  if (live.isLoading) return null;
  if (liveHere ? liveDismissed === raceKey : tipDismissed) return null;

  const dismiss = () => {
    if (liveHere) {
      write(LIVE_KEY, raceKey);
      setLiveDismissed(raceKey);
    } else {
      write(TIP_KEY, '1');
      setTipDismissed(true);
    }
  };

  const followButton = (
    <Button
      component={RouterLink}
      to="/live"
      onClick={onOpen}
      variant="contained"
      size="small"
      startIcon={<Timeline />}
      sx={{ flexShrink: 0 }}
    >
      Follow live
    </Button>
  );

  return (
    <Box
      sx={{
        mb: 2,
        py: 1.25,
        pl: 2,
        pr: 1,
        borderRadius: 1.5,
        border: 1,
        borderColor: liveHere ? alpha(LIVE_RED, 0.45) : 'divider',
        background: (t) =>
          liveHere
            ? `linear-gradient(90deg, ${alpha(LIVE_RED, t.palette.mode === 'dark' ? 0.12 : 0.08)}, transparent 70%)`
            : alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.07 : 0.05),
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      {liveHere ? (
        <Box
          component="span"
          sx={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            bgcolor: LIVE_RED,
            boxShadow: `0 0 0 4px ${alpha(LIVE_RED, 0.25)}`,
            flexShrink: 0,
          }}
        />
      ) : (
        <Timeline sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {liveHere ? `${site} is live: follow this slip` : 'Follow your bets live on race day'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
          {liveHere
            ? `Lap ${race?.lap}/${race?.laps}. Each bet's chance, lap by lap.`
            : 'Check back while the race is live to see how your picks are doing lap by lap.'}
        </Typography>
        {liveHere && <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 1 }}>{followButton}</Box>}
      </Box>
      {liveHere && <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{followButton}</Box>}
      <IconButton size="small" aria-label="Dismiss" onClick={dismiss} sx={{ flexShrink: 0, alignSelf: 'flex-start' }}>
        <Close fontSize="small" />
      </IconButton>
    </Box>
  );
};
